/* eslint-disable react/no-array-index-key */
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatting } from 'locale';
import Divider from 'ui-library/components/Divider';
import { Title } from 'ui-library';
import { DocStatus, DocTypes } from 'components/DocBox/DocBox.types';
import { AdvisorySectionProps } from './AdvisorySection.types';
import DocBox from '../../DocBox';
import './AdvisorySection.css';

const AdvisorySection: FC<AdvisorySectionProps> = ({
    data,
    isExpired,
    isAdvisoryCase,
    proofOfAssetDocuments,
    advisoryDocument,
    basicAgreementDocuments,
    showGeneralDocumentsAndProofOfAssetsSection,
    hasDocumentESignAccess,
    isJA,
    jaUsers,
    onManualSign,
    onESign,
    onIdDocumentUpload,
    canReuploadBasicAgreement,
    showIdDocuments = false,
    idDocuments = [{
        contactId: '',
        documentId: '',
        documentName: 'Id Document',
        status: 'ready-to-sign',
        date: '',
    }],
    canReUploadAdvisoryDocs = false,
    onAdvisoryDocReUpload,
}) => {
    const { t } = useTranslation();
    const {
        advisoryNo,
        createdAt,
        validTill,
        investmentAmount,
    } = data;

    const {
        getAmountWithDecimals,
    } = useFormatting();

    return (
        <div className={`${(isJA || isAdvisoryCase || showGeneralDocumentsAndProofOfAssetsSection) ? 'advisory-section' : ''}`}>
            {isJA && (
                <div className="ja-section">
                    <Title type="3">
                        {t('ApplicationDetails.JointAccount.Title')}
                    </Title>
                    <p className="ja-info-text">{`${jaUsers?.[0]?.fullName}, ${jaUsers?.[1]?.fullName}`}</p>
                </div>
            )}
            {isAdvisoryCase && (
                <Title type="3">
                    {t('ApplicationDetails.AdvisorySection.Title')}
                </Title>
            )}

            <div className="advisory-section--grid mb-16">
                {isAdvisoryCase && (
                    <>
                        <div key="text-data" className="advisory-section--grid-col">
                            <div className="application-info-grid">
                                <div className="application-info-grid-col">
                                    <Title type="5">
                                        {t('ApplicationDetails.AdvisorySection.AdvisoryNo')}
                                    </Title>
                                    {advisoryNo}
                                </div>
                                <Divider direction="vertical"/>
                                <div className="application-info-grid-col">
                                    <Title type="5">
                                        {t('ApplicationDetails.AdvisorySection.CreatedAt')}
                                    </Title>
                                    {createdAt}
                                </div>
                                <div className="application-info-grid-col">
                                    <Title type="5">
                                        {t('ApplicationDetails.AdvisorySection.ValidTill')}
                                    </Title>
                                    <span className={isExpired ? 'color-danger' : ''}>{validTill}</span>
                                </div>
                                <Divider direction="vertical"/>
                                <div className="application-info-grid-col">
                                    <Title type="5">
                                        {t('ApplicationDetails.AdvisorySection.InvestmentAmount')}
                                    </Title>
                                    {getAmountWithDecimals(investmentAmount)}
                                </div>
                            </div>
                        </div>

                        <div key="doc" className="advisory-section--grid-col">
                            <DocBox
                                type={DocTypes.ADVISORY}
                                uploadStatus={advisoryDocument?.status}
                                documentName={advisoryDocument?.documentName}
                                documentId={advisoryDocument?.documentId}
                                canReUpload={canReUploadAdvisoryDocs}
                                onAdvisoryDocReUpload={onAdvisoryDocReUpload}
                            />
                        </div>
                    </>
                )}
                {showGeneralDocumentsAndProofOfAssetsSection && (
                    <div className="flex-column general-docs-wrapper">
                        <div className="advisory-section--grid">
                            {!!basicAgreementDocuments.length && (
                                <div className="advisory-section--grid-col">
                                    <Title type="3">
                                        {t('ApplicationDetails.AdvisorySection.BasicAgreement.notAvailableName')}
                                    </Title>
                                    {basicAgreementDocuments.map((doc, index) => (
                                        <DocBox
                                            key={`basic-${doc?.documentId ?? ''}-${index}`}
                                            type={DocTypes.BASIC_AGREEMENT}
                                            contactId={doc?.contactId}
                                            uploadStatus={doc?.status}
                                            documentName={doc?.documentName}
                                            documentId={doc?.documentId}
                                            onManualSign={onManualSign}
                                            onESign={onESign}
                                            canESign={hasDocumentESignAccess}
                                            canReUpload={canReuploadBasicAgreement && doc?.status !== DocStatus.Disabled}
                                        />
                                    ))}
                                </div>
                            )}
                            {!!proofOfAssetDocuments.length && (
                                <div className="advisory-section--grid-col">
                                    <Title type="3">
                                        {t('ApplicationDetails.AdvisorySection.ProofOfAssets.notAvailableName')}
                                    </Title>
                                    {proofOfAssetDocuments?.map((doc, index) => (
                                        <DocBox
                                            key={`poa-${doc?.documentId ?? ''}-${index}`}
                                            type={DocTypes.PROOF_OF_ASSETS}
                                            contactId={doc?.contactId}
                                            uploadStatus={doc.status}
                                            documentId={doc?.documentId}
                                            documentName={doc.documentName}
                                            originOfAsset={doc?.originOfAsset}
                                            onManualSign={onManualSign}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {showIdDocuments && (
                    <div className="flex-column general-docs-wrapper">
                        <Title type="3">
                            {t('3b.kyc.idOrPassport')}
                        </Title>
                        <div className="advisory-section--grid">
                            <div className="advisory-section--grid-col">
                                {jaUsers?.map(user => idDocuments?.filter(idDoc => idDoc?.contactId === user?.id)?.map((doc, index) => (
                                    <DocBox
                                        key={`id-${user?.id ?? ''}-${doc?.documentId ?? ''}-${index}`}
                                        type={DocTypes.ID_DOCUMENT}
                                        contactId={doc?.contactId as number}
                                        uploadStatus={doc.status as DocStatus}
                                        documentId={doc?.documentId as number}
                                        documentName={`${doc.documentName} ${jaUsers?.length > 1 ? user?.fullName : ''} ${` ${doc?.date || ''}`}`}
                                        onManualSign={(docData) => onIdDocumentUpload({ userId: user?.id, docData })}
                                        allowSigning={false}
                                        canReUpload
                                    />
                                )))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdvisorySection;
